import { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import { gerarTicketIngresso, salvarOuCompartilhar } from '@/pages/carteira/utils';
import { gerarConvite } from '@/lib/conviteImagem';
import { formatarDataHora, linkWhatsAppMensagem } from '../utils';
import type { InscritoGerenciar } from '../types';

interface Props {
  inscrito: InscritoGerenciar;
  eventoNome: string;
  eventoInicio: string;
  eventoLocal: string | null;
  aoFechar: () => void;
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

function nomeArquivoDe(nome: string): string {
  const base = (nome || 'ingresso').trim().replace(/\s+/g, '-').toLowerCase();
  return `ingresso-${base}.png`;
}

// Gera o ingresso de quem nunca recebeu o QR: mesmo desenho da carteira,
// com o QR do código da inscrição. Nada é enviado ao banco.
export default function FolhaConvite({
  inscrito,
  eventoNome,
  eventoInicio,
  eventoLocal,
  aoFechar,
}: Props) {
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const qrId = `qr-convite-${inscrito.inscricao_id}`;
  const dataHora = eventoInicio ? formatarDataHora(eventoInicio) : '';
  const nomeArquivo = nomeArquivoDe(inscrito.participante);
  const textoComprovante = [
    `Ingresso · ${eventoNome}`,
    `${dataHora}${eventoLocal ? ` · ${eventoLocal}` : ''}`,
    `Portador: ${inscrito.participante}`,
    inscrito.modalidade,
  ].join('\n');

  const telefone = (inscrito.participante_telefone ?? '').replace(/\D/g, '');
  const temTelefone = telefone.length > 0;
  const mensagem = `Olá, ${primeiroNome(inscrito.participante)}! Segue o seu ingresso do ${eventoNome}. Mostre o QR Code na entrada.`;
  const linkWhats = linkWhatsAppMensagem(inscrito.participante_telefone ?? '', mensagem);

  async function gerarCanvas(): Promise<HTMLCanvasElement | null> {
    const qrCanvas = document.getElementById(qrId) as HTMLCanvasElement | null;
    if (!qrCanvas) return null;
    await document.fonts.ready;
    try {
      return await gerarConvite({
        evento: eventoNome,
        quando: dataHora,
        local: eventoLocal,
        rotulo: 'Ingresso',
        nome: inscrito.participante,
        detalhe: inscrito.modalidade,
        codigo: inscrito.codigo,
      });
    } catch {
      // plano B: o desenho antigo, igual ao de hoje
      return gerarTicketIngresso({
        evento: eventoNome,
        dataHora,
        local: eventoLocal,
        portador: inscrito.participante,
        participacao: inscrito.modalidade,
        cpf: inscrito.cpf,
        codigo: inscrito.codigo,
        qrCanvas,
      });
    }
  }

  async function baixar() {
    setErro(null);
    setGerando(true);
    try {
      const canvas = await gerarCanvas();
      if (!canvas) {
        setErro('Não foi possível preparar o convite. Tente de novo.');
        return;
      }
      await salvarOuCompartilhar(canvas, nomeArquivo, textoComprovante);
    } catch {
      setErro('Não foi possível gerar o convite. Tente de novo.');
    } finally {
      setGerando(false);
    }
  }

  async function enviarWhats() {
    setErro(null);
    setGerando(true);
    try {
      const canvas = await gerarCanvas();
      if (!canvas) {
        setErro('Não foi possível preparar o convite. Tente de novo.');
        return;
      }
      await salvarOuCompartilhar(canvas, nomeArquivo, textoComprovante);
      window.open(linkWhats, '_blank', 'noopener,noreferrer');
    } catch {
      setErro('Não foi possível gerar o convite. Tente de novo.');
    } finally {
      setGerando(false);
    }
  }

  return (
    <Modal aberto titulo="Gerar convite" aoFechar={aoFechar}>
      {/* QR escondido: só existe para desenhar a imagem do convite. */}
      <div
        className="pointer-events-none fixed left-0 top-0 h-0 w-0 overflow-hidden opacity-0"
        aria-hidden
      >
        <QRCodeCanvas
          id={qrId}
          value={inscrito.codigo}
          size={512}
          level="M"
          marginSize={2}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </div>

      <p className="text-[16px] font-semibold text-foreground-950">{inscrito.participante}</p>
      <p className="mt-1 text-[14px] text-foreground-600">
        {eventoNome}
        {dataHora ? ` · ${dataHora}` : ''}
        {eventoLocal ? ` · ${eventoLocal}` : ''}
      </p>
      <p className="mt-3 text-[14px] text-foreground-600">
        O convite sai igual ao ingresso dos outros compradores, com o QR Code para a entrada.
      </p>

      {erro && (
        <p className="mt-3 rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</p>
      )}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Botao
          variante="primario"
          tamanho="lg"
          cheio
          icone="ri-download-2-line"
          carregando={gerando}
          onClick={baixar}
        >
          Baixar convite
        </Botao>
        {temTelefone && (
          <Botao
            variante="whatsapp"
            tamanho="lg"
            cheio
            icone="ri-whatsapp-line"
            carregando={gerando}
            onClick={enviarWhats}
          >
            Enviar no WhatsApp
          </Botao>
        )}
      </div>
    </Modal>
  );
}